import React, { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import {
  useCreateIncidentAction,
  useUpdateIncidentAction,
  GetIncidentActionResponse,
} from '../hooks/incidentActionHooks';
import { useActualIncidentId } from '../hooks/commonHooks';
import dayjs from 'dayjs';
import { IncidentObjective } from '../hooks/incidentObjectiveHooks';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';

type AddActionCardProps = {
  objective?: IncidentObjective | null;
  parentObjectiveOptions?: Array<{ value: string; label: string | undefined }>;
  actionOptions: Array<{ value: string; label: string }>;
  handleCancel: () => void;
  action?: any;
  editing?: boolean;
};
type ShowActionCardProps = {
  action: GetIncidentActionResponse;
  index: number;
  objective?: IncidentObjective;
  parentObjectiveOptions: Array<{ value: string; label: string | undefined }>;
  actionOptions: Array<{ value: string; label: string }>;
};

export const AddActionCard = ({
  objective,
  actionOptions,
  handleCancel,
  action,
  editing,
}: AddActionCardProps) => {
  const incidentId = useActualIncidentId();

  const parent = action?.parent_id || objective?.objective_id || null;
  const [date, setDate] = useState<Date | null>(
    action?.actual_date || new Date()
  );
  const [name, setName] = useState<string>(action?.name || '');
  const [status, setStatus] = useState<string>(action?.status || 'Planned');

  const createIncidentActionMutation = useCreateIncidentAction();
  const updateIncidentAction = useUpdateIncidentAction();

  const handleAddAction = async () => {
    const payload = {
      incident_id: incidentId,
      description: '',
      parentId: parent === '' ? null : parent,
      status: status,
      actual_date: date,
      name: name,
      checklist: [],
    };
    if (editing) {
      await updateIncidentAction.mutateAsync({
        ...action,
        name: name,
        description: action?.description || '',
        checklist: action?.checklist || [],
        parent_id: parent === '' ? null : parent,
        status: status,
        actual_date: date || new Date(),
      });
      handleCancel();
      return;
    }
    await createIncidentActionMutation.mutateAsync(payload);
    handleCancel();
  };

  return (
    <div className="bordered-card">
      <div className="d-flex flex-wrap align-items-start mb-3 gap-3">
        <div className="flex-grow-1">
          <StylishNewTextArea
            value={name}
            maxLength={200}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="Enter action description..."
          />
        </div>
        <div
          className="d-flex align-items-center flex-wrap gap-2"
          style={{ minWidth: 0 }}
        >
          <StylishNewSelect
            options={actionOptions}
            defaultValue={
              action
                ? { value: action?.status, label: action?.status }
                : actionOptions[0]
            }
            onChange={(e) => setStatus(e?.value)}
          />

          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <StylishDateTimePicker
              type="datetime-local"
              disableDateTimeLabel
              value={action?.actual_date || date}
              onChange={(e) => {
              setDate(e ? new Date(e) : null);
              }}
            />
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-end">
        <div>
          <StylishNewButton
            className="me-2"
            onClick={() => handleAddAction()}
            disabled={!name}
          >
            {editing ? 'Save' : 'Add'}
          </StylishNewButton>
          <StylishNewButton
            className="button--secondary"
            onClick={() => handleCancel()}
          >
            Cancel
          </StylishNewButton>
        </div>
      </div>
    </div>
  );
};

export const ShowActionCard = ({
  action,
  index,
  objective,
  parentObjectiveOptions,
  actionOptions,
}: ShowActionCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const updateIncidentAction = useUpdateIncidentAction();

  const handleEdit = () => {
    setIsEditing(!isEditing);
  };
  const handleDelete = async () => {
    await updateIncidentAction.mutateAsync({
      ...action,
      archived: true,
    });
  };
  return (
    <>
      {isEditing ? (
        <AddActionCard
          objective={objective ?? null}
          parentObjectiveOptions={parentObjectiveOptions}
          actionOptions={actionOptions}
          handleCancel={() => setIsEditing(false)}
          action={action}
          editing={true}
        />
      ) : (
        <div
          className={
            'bordered-card d-flex justify-content-between align-items-flex-start mb-2'
          }
        >
          <div>
            Action {index + 1} -{' '}
            <span className="item-name">{action.name}</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            {dayjs(action.actual_date).isValid()
              ? dayjs(action.actual_date).format('MMM D, YYYY HH:mm')
              : '—'}{' '}
            {action.status}
            <StylishNewButton
              className="button--link action-button"
              onClick={() => handleEdit()}
            >
              <SharedIcon iconName="edit" size="sm" />
            </StylishNewButton>
            <StylishNewButton
              className="button--link action-button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <SharedIcon iconName="delete" size="sm" />
            </StylishNewButton>
          </div>
        </div>
      )}
    </>
  );
};
